import { getPlanQueryParameter, mapModeStore } from '$lib/core/map/map-mode-store';
import type { IInsertMastPointPayload } from '$lib/models/IInsertMastPointModel';
import type { ILineDataModel, LinemarkingModel, SpanModel } from '$lib/models/ILineDataModel';
import { get } from 'svelte/store';
import api from './kyAuthenticatedInstance';
import type { ContextualizedCzmlResponse } from '$lib/models/IProjectModel';
import type { INewLinesPayload } from '$lib/models/INewLinesPayload';

const SearchService = {
	async searchEntities<T>({ searchText }: { searchText: string }): Promise<T> {
		const planIds = [];
		const activePlanId = get(mapModeStore).activeDataSource?.name.split(':');
		if (activePlanId) planIds.push(parseInt(activePlanId[1]));

		return api
			.post(`MastPoint/Search`, {
				json: {
					planIds,
					searchText
				}
			})
			.json();
	}
};

const MastPointService = {
	async createMastPoint<T>(mastpoint: T): Promise<Response> {
		return api.post(`MastPoint/czml` + get(getPlanQueryParameter), {
			json: {
				...mastpoint
			}
		});
	},
	/**
	 * Inserts a mastpoint into an existing line. The backend splits the line and connects the new
	 * mastpoint to both neighbours, and responds with contextualized CZML like `createMastPoint`.
	 */
	async insertMastPoint(payload: IInsertMastPointPayload): Promise<Response> {
		return api.post(`MastPoint/insert/czml` + get(getPlanQueryParameter), {
			json: payload
		});
	},
	async updateMastPointPosition({
		updatedMastPoint,
		moveConnectedSections = false
	}: {
		updatedMastPoint: { 
            id: number,
            lat: number,
            lng: number;
        }
		moveConnectedSections: boolean;
	}): Promise<Response> {
		return api.put(
			`MastPoint/UpdatePosition/${updatedMastPoint.id}/czml${get(getPlanQueryParameter)} `,
			{
				json: {
					...updatedMastPoint,
					options: {
						moveConnectedSections
					}
				}
			}
		);
	}
};

const LineService = {
	async fetchLines(): Promise<ILineDataModel[]> {
		return api.get(`Lines`).json<ILineDataModel[]>();
	},
	async fetchLineSpan<T>(lineId: number): Promise<T> {
		return api.get(`Lines/${lineId}/span` + get(getPlanQueryParameter)).json<T>();
	},
	/*async createLine(lineData: ILineDataModel): Promise<Response> {
		return api.post(`Lines`, {
			json: {
				...lineData
			}
		});
	},*/
	async createLines(lineData: {
		inputLines: INewLinesPayload.InputLineGroup[];
	}): Promise<Response> {
		return api.post(`Lines/multi/czml` + get(getPlanQueryParameter), {
			json: {
				...lineData
			}
		});
	},
	/*async updateLine(lineData: ILineDataModel['data']): Promise<Response> {
		return api.put(`Lines/${lineData.id}`, {
			json: {
				...lineData
			}
		});
	},*/
	async updateLineSpan(lineData: SpanModel.ILineSpanDataModel): Promise<ContextualizedCzmlResponse> {
		return api.put(`Lines/span` + get(getPlanQueryParameter), {
			json: {
				...lineData
			}
		}).json();
	},
	async updateLineMarking(lineData: LinemarkingModel.SpanModel): Promise<ContextualizedCzmlResponse> {
		return api.put(`Lines/span/linemarking` + get(getPlanQueryParameter), {
			json: {
				...lineData
			}
		}).json();
	},
	/*async deleteLine(lineId: number): Promise<Response> {
		return api.delete(`Lines/${lineId}`);
	},*/
	async deleteLineSpan(spanId: number): Promise<Response> {
		return api.delete(`Lines/span/${spanId}` + get(getPlanQueryParameter));
	},
	async autoOrganizeLines(mastPointId: number): Promise<Response> {
		return api.put(`MastPoint/${mastPointId}/lines/offsets` + get(getPlanQueryParameter));
	}
};

export const PoleAndLineService = {
	...MastPointService,
	...LineService,
	...SearchService
};
